import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"
import { cn } from "../../lib/utils"

const SelectSearchContext = React.createContext({ query: "", setQuery: () => {} })

function Select({ children, onOpenChange, onSearchChange, filterOptions = true, ...props }) {
  const [query, setQueryState] = React.useState("")
  const setQuery = (value) => {
    setQueryState(value)
    onSearchChange?.(value)
  }
  React.useEffect(() => { setQueryState("") }, [props.open])
  return (
    <SelectSearchContext.Provider value={{ query, setQuery, filterOptions }}>
      <SelectPrimitive.Root {...props} onOpenChange={(open) => {
        setQuery("")
        onOpenChange?.(open)
      }}>
        {children}
      </SelectPrimitive.Root>
    </SelectSearchContext.Provider>
  )
}

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef(
  (
    {
      className,
      children,
      position = "popper",
      viewportClassName,
      onViewportScroll,
      searchPlaceholder = "Search options...",
      loading = false,
      onKeyDown,
      style,
      ...props
    },
    ref
  ) => {
    const { query, setQuery } = React.useContext(SelectSearchContext)
    return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-50 max-h-96 min-w-32 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        style={{
          maxHeight: position === "popper"
            ? "min(24rem, var(--radix-select-content-available-height))"
            : undefined,
          ...style,
        }}
        {...props}
        onKeyDown={(event) => {
          onKeyDown?.(event)
          if (event.defaultPrevented || event.target.tagName === "INPUT") return
          if (event.key.length === 1 && event.key !== " " && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault()
            setQuery(event.key)
            event.currentTarget.querySelector('input[type="search"]')?.focus()
          }
        }}
      >
        <div className="shrink-0 border-b p-2">
          <input data-page-search="true"
            type="search"
            aria-label={searchPlaceholder}
            placeholder={searchPlaceholder}
            value={query}
            className="h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 text-sm outline-hidden focus:ring-1 focus:ring-ring"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              // Keep typing and cursor movement out of Radix typeahead.
              if (event.key === "Escape" || event.key === "Tab") return
              event.stopPropagation()
              if (event.key === "Enter") event.preventDefault()
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault()
                const options = event.currentTarget.closest('[role="listbox"]')
                  ?.querySelectorAll('[role="option"]:not([hidden]):not([data-disabled])')
                const option = event.key === "ArrowDown" ? options?.[0] : options?.[options.length - 1]
                option?.focus()
              }
            }}
          />
        </div>
        {/* Reserve arrow space so scrolling does not resize/reposition the menu. */}
        <div className="h-6 shrink-0"><SelectScrollUpButton /></div>
        <SelectPrimitive.Viewport
          className={cn(
            "min-h-0 overscroll-contain p-1 [&:has([role=option]:not([hidden]))>.select-empty]:hidden",
            position === "popper" &&
              "w-full min-w-(--radix-select-trigger-width)",
            viewportClassName
          )}
          onScroll={onViewportScroll}
        >
          {children}
          <div hidden={loading} className="select-empty px-2 py-3 text-sm text-muted-foreground" role="status">
            No options found.
          </div>
        </SelectPrimitive.Viewport>
        <div className="h-6 shrink-0"><SelectScrollDownButton /></div>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
    )
  }
)
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectItem = React.forwardRef(({ className, children, textValue, disabled, hidden, style, ...props }, ref) => {
  const { query, filterOptions = true } = React.useContext(SelectSearchContext)
  const textRef = React.useRef(null)
  const [label, setLabel] = React.useState("")
  React.useLayoutEffect(() => {
    setLabel(textRef.current?.textContent || "")
  }, [children])
  const filtered = filterOptions && Boolean(query.trim()) && !(textValue ?? label)
    .toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
  return (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-hidden focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
      className
    )}
    {...props}
    textValue={textValue}
    disabled={disabled || filtered || hidden}
    hidden={hidden || filtered}
    style={{ ...style, ...((hidden || filtered) ? { display: "none" } : {}) }}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText ref={textRef}>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
  )
})
SelectItem.displayName = SelectPrimitive.Item.displayName


// Adapts legacy option children and event handlers to the shared searchable select.
const FormSelect = React.forwardRef(function FormSelect({
  children, value, defaultValue, onChange, onBlur, name, id, disabled,
  required, form, className, style, onOpenChange, ...props
}, ref) {
  const triggerRef = React.useRef(null)
  React.useImperativeHandle(ref, () => triggerRef.current)
  const options = []
  function collect(nodes) {
    React.Children.forEach(nodes, (child) => {
      if (!React.isValidElement(child)) return
      if (child.type === "option") options.push(child)
      else collect(child.props.children)
    })
  }
  collect(children)
  const optionValue = (option) => String(option.props.value ?? option.props.children ?? "")
  const [internalValue, setInternalValue] = React.useState(() =>
    String(defaultValue ?? options.find((option) => !option.props.disabled)?.props.value ?? "")
  )
  const selectedValue = String(value ?? internalValue)
  // Encode every value so an empty option remains selectable without a sentinel collision.
  const encode = (raw) => "option:" + raw
  const eventFor = (nextValue) => {
    const target = { name, id, value: nextValue, type: "select-one" }
    return { target, currentTarget: target }
  }
  return (
    <Select
      value={encode(selectedValue)}
      disabled={disabled}
      onOpenChange={onOpenChange}
      onValueChange={(encoded) => {
        const nextValue = encoded.slice(7)
        setInternalValue(nextValue)
        onChange?.(eventFor(nextValue))
      }}
    >
      {name && <input type="hidden" name={name} value={selectedValue} disabled={disabled} form={form} />}
      {required && <input
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        value={selectedValue}
        required
        disabled={disabled}
        form={form}
        onChange={() => {}}
        onInvalid={(event) => { event.preventDefault(); triggerRef.current?.focus() }}
      />}
      <SelectTrigger
        {...props}
        ref={triggerRef}
        id={id}
        className={className}
        style={style}
        aria-required={required || undefined}
        onBlur={() => onBlur?.(eventFor(selectedValue))}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option, index) => (
          <SelectItem
            key={encode(optionValue(option)) + ":" + index}
            value={encode(optionValue(option))}
            disabled={option.props.disabled}
            hidden={option.props.hidden}
          >
            {option.props.children}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
})

export {
  FormSelect,
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
